import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

/**
 * Emitted by the callout's "Generate" / "Improve notes" action. The host opens
 * the AI writing overlay using these fields as the seed.
 */
export interface AskAiSubmission {
    prompt: string;
    useNotesAsContext: boolean;
}

/**
 * "Ask AI to write something" centered modal. Adapts based on whether the
 * host's notes field has content:
 *  - Empty notes      → no toggle, "Describe what you need.", "Generate".
 *  - Notes + toggleOn → toggle ON, "Tell AI what to do with your notes.", "Improve notes".
 *  - Notes + toggleOff → toggle OFF, hint reverts to empty-notes copy, "Generate".
 *
 * The host renders one instance and toggles `open` to drive lifecycle.
 *
 * Centred on the SCREEN, like the app's other dialogs, not on the host's notes box - which is
 * where it used to land (inside the notes column, or the 100px-tall step drawer). `position:
 * fixed` alone cannot do that here: the composer and task sliders carry `transform:
 * translate(0,0)`, which makes them the containing block for every fixed descendant, so the
 * dialog would centre in the panel instead. The element therefore parks itself on `<body>`
 * while open and goes home on close - the pattern `vf-modal-shell` and the parked delete
 * confirm already use. Angular keeps owning the node (only its DOM parent moves), so bindings,
 * outputs and change detection are unaffected. No host changes are needed.
 */
@Component({
    selector: 'vf-write-with-ai-callout',
    templateUrl: './write-with-ai-callout.component.html',
    styleUrls: ['./write-with-ai-callout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WriteWithAiCalloutComponent implements OnChanges, OnDestroy {

    @Input() open: boolean = false;
    @Input() hasNotesContent: boolean = false;

    @Output() closed: EventEmitter<void> = new EventEmitter<void>();
    @Output() submitted: EventEmitter<AskAiSubmission> = new EventEmitter<AskAiSubmission>();

    @ViewChild('promptTextarea', { static: false }) promptTextarea!: ElementRef<HTMLTextAreaElement>;

    public prompt: string = '';
    public useNotesAsContext: boolean = false;

    /** Where the element lives when it is not parked on `<body>`; null while at home. */
    private _home: Node | null = null;

    constructor(
        private _cdr: ChangeDetectorRef,
        private _host: ElementRef<HTMLElement>,
        @Inject(DOCUMENT) private _doc: Document
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['open']) {
            if (changes['open'].currentValue === true) {
                this._park();
                this._resetForOpen();
                setTimeout(() => {
                    if (this.promptTextarea && this.promptTextarea.nativeElement) {
                        this.promptTextarea.nativeElement.focus();
                    }
                }, 0);
            } else {
                this._unpark();
            }
        }
    }

    ngOnDestroy(): void {
        this._unpark();
    }

    /** Move the element under `<body>` so `position: fixed` resolves against the viewport. */
    private _park(): void {
        const el: HTMLElement = this._host.nativeElement;
        if (!el || this._home || !el.parentNode) {
            return;
        }
        this._home = el.parentNode;
        this._doc.body.appendChild(el);
    }

    /** Put the element back where the host template rendered it. Safe to call twice. */
    private _unpark(): void {
        const el: HTMLElement = this._host.nativeElement;
        if (el && this._home) {
            this._home.appendChild(el);
        }
        this._home = null;
    }

    public get hintText(): string {
        if (this.hasNotesContent && this.useNotesAsContext) {
            return 'Tell AI what to do with your notes.';
        }
        return 'Describe what you need.';
    }

    public get placeholderText(): string {
        if (this.hasNotesContent && this.useNotesAsContext) {
            return 'e.g. Turn this into a polished summary. Or: Extract action items. Or: Rewrite as a client-facing email.';
        }
        return 'e.g. Write a user story for the foundation inspection task — include acceptance criteria and a list of risks.';
    }

    public get toggleHint(): string {
        if (this.useNotesAsContext) {
            return 'AI will read your notes and improve them.';
        }
        return 'AI will ignore your notes and write from scratch.';
    }

    public get actionLabel(): string {
        // Always "Generate" — was previously branched on
        // hasNotesContent+useNotesAsContext to read "Improve notes" but
        // product unified the button label across both states.
        return 'Generate';
    }

    public get canSubmit(): boolean {
        return (this.prompt || '').trim().length > 0;
    }

    public toggleContext(): void {
        this.useNotesAsContext = !this.useNotesAsContext;
    }

    public onBackdropMouseDown(ev: MouseEvent): void {
        if (ev.target === ev.currentTarget) {
            this.close();
        }
    }

    public submit(): void {
        if (!this.canSubmit) {
            return;
        }
        let payload: AskAiSubmission = {
            prompt: (this.prompt || '').trim(),
            useNotesAsContext: this.hasNotesContent && this.useNotesAsContext
        };
        this.submitted.emit(payload);
    }

    public close(): void {
        this.closed.emit();
    }

    @HostListener('document:keydown', ['$event'])
    public onKeydown(ev: KeyboardEvent): void {
        if (!this.open) {
            return;
        }
        if (ev.key === 'Escape') {
            ev.preventDefault();
            this.close();
            return;
        }
        if ((ev.metaKey || ev.ctrlKey) && ev.key === 'Enter') {
            ev.preventDefault();
            this.submit();
        }
    }

    public onTextareaKeydown(ev: KeyboardEvent): void {
        if ((ev.metaKey || ev.ctrlKey) && ev.key === 'Enter') {
            ev.preventDefault();
            // stopPropagation so the document-level @HostListener doesn't also
            // fire submit() and double-trigger the AI request.
            ev.stopPropagation();
            this.submit();
        }
    }

    private _resetForOpen(): void {
        this.prompt = '';
        this.useNotesAsContext = this.hasNotesContent;
        this._cdr.markForCheck();
    }
}
